import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Loader2, Eye, EyeOff, Shield, Check, X, ArrowRight, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const isFirstLogin = location.state?.firstLogin || false;

  // Password strength validation
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    match: newPassword === confirmPassword && newPassword.length > 0,
  };

  const isValidPassword = Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidPassword) {
      toast.error('Sila pastikan password memenuhi semua syarat');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user data in localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.must_change_password = false;
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Password berjaya ditukar!');
      navigate('/my-scorecard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menukar password');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-xs transition-all duration-300 ${valid ? 'text-emerald-400' : 'text-gray-500'}`}>
      {valid ? (
        <Check className="w-3 h-3" />
      ) : (
        <X className="w-3 h-3" />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#030308]">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a12] via-[#030308] to-[#050510]" />
        
        {/* Animated glow orbs */}
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-gradient-to-r from-emerald-500/20 to-cyan-500/10 blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-[-30%] right-[-10%] w-[700px] h-[700px] rounded-full bg-gradient-to-r from-blue-500/15 to-purple-500/10 blur-[150px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        
        {/* Grid overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8 space-y-4">
            <div className="relative inline-block">
              <div className="absolute inset-0 blur-xl bg-gradient-to-r from-emerald-400/30 to-cyan-500/30 rounded-full" />
              <div className="relative bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 p-4 rounded-2xl border border-emerald-500/20">
                <Shield className="w-12 h-12 text-emerald-400" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
                {isFirstLogin ? 'Tukar Password Anda' : 'Kemaskini Password'}
              </h1>
              <p className="text-sm text-gray-400">
                {isFirstLogin 
                  ? 'Untuk keselamatan akaun, sila tetapkan password baru anda'
                  : 'Masukkan password semasa dan password baru anda'
                }
              </p>
            </div>
          </div>

          {/* Form Card */}
          <div className="relative group">
            {/* Card glow effect */}
            <div className="absolute -inset-[1px] bg-gradient-to-r from-emerald-500/50 via-cyan-500/50 to-blue-500/50 rounded-2xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[#0a0a12]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Current Password */}
                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Lock size={12} className="text-emerald-400" /> Password Semasa
                  </Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="bg-white/5 border-white/10 focus:border-emerald-500/50 focus:ring-emerald-500/20 text-white rounded-xl pl-4 pr-12 py-3 transition-all duration-300"
                      placeholder="••••••••"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-emerald-400 transition-colors"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Lock size={12} className="text-cyan-400" /> Password Baru
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="bg-white/5 border-white/10 focus:border-cyan-500/50 focus:ring-cyan-500/20 text-white rounded-xl pl-4 pr-12 py-3 transition-all duration-300"
                      placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400 transition-colors"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Lock size={12} className="text-blue-400" /> Sahkan Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="bg-white/5 border-white/10 focus:border-blue-500/50 focus:ring-blue-500/20 text-white rounded-xl pl-4 pr-12 py-3 transition-all duration-300"
                      placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-400 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="bg-white/5 rounded-xl p-4 space-y-2">
                  <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">Syarat Password:</p>
                  <PasswordCheck valid={passwordChecks.length} text="Minimum 8 aksara" />
                  <PasswordCheck valid={passwordChecks.uppercase} text="Sekurang-kurangnya 1 huruf besar" />
                  <PasswordCheck valid={passwordChecks.lowercase} text="Sekurang-kurangnya 1 huruf kecil" />
                  <PasswordCheck valid={passwordChecks.number} text="Sekurang-kurangnya 1 nombor" />
                  <PasswordCheck valid={passwordChecks.match} text="Password sama" />
                </div>

                <Button
                  type="submit"
                  className="w-full relative overflow-hidden group/btn bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-400 hover:to-cyan-500 text-white font-medium py-3 rounded-xl transition-all duration-300 shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !isValidPassword}
                  data-testid="submit-button"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Menyimpan...</>
                    ) : (
                      <>
                        Tukar Password
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700" />
                </Button>

                {!isFirstLogin && (
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="w-full text-sm text-gray-400 hover:text-cyan-400 transition-colors"
                    data-testid="cancel-button"
                  >
                    Batal
                  </button>
                )}
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-600">
              © 2025 Underads. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
